import { z } from "zod";
import { isoDateTimeString, paginationQuerySchema, uuidSchema } from "./common";

export const notificationSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  type: z.string(),
  title: z.string(),
  body: z.string(),
  // Relative deep-link path (e.g. "/employer/postings/abc"). Web router
  // and mobile FCM tap handler both honour this — never derive client-side.
  actionUrl: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
  readAt: isoDateTimeString.nullable().optional(),
  createdAt: isoDateTimeString,
});

export const notificationsQuerySchema = paginationQuerySchema;

export const notificationReadResponseSchema = z.object({
  notification: notificationSchema,
});

export const notificationsListResponseSchema = z.object({
  items: z.array(notificationSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int().min(0),
});

export const notificationsReadAllResponseSchema = z.object({
  updated: z.number().int().min(0),
});

export type Notification = z.infer<typeof notificationSchema>;
export type NotificationsListResponse = z.infer<
  typeof notificationsListResponseSchema
>;
export type NotificationsReadAllResponse = z.infer<
  typeof notificationsReadAllResponseSchema
>;

// ---------------------------------------------------------------------------
// Notification templates (admin) + custom-push send
// ---------------------------------------------------------------------------

// Mirror of Prisma enum NotificationType. Kept as a literal union so the
// admin UI can iterate it without importing @chotca/db on the client.
export const notificationTypeSchema = z.enum([
  "CV_APPLIED",
  "INVITE_RECEIVED",
  "INVITE_ACCEPTED",
  "INVITE_COMMITTED",
  "INVITE_CANCELLED",
  "INVITE_EXPIRED",
  "INTERVIEW_REMINDER_24H",
  "APPLICATION_REJECTED",
  "APPLICATION_EXPIRED",
  "KYC_APPROVED",
  "KYC_REJECTED",
  "CREDIT_PURCHASE_PAID",
  "PLAN_PURCHASE_PAID",
  "SUBSCRIPTION_EXPIRING_SOON",
  "POSTING_EXPIRING",
  "POSTING_NEW_NEAR",
  "POSTING_MATCHED",
  "NEW_POSTING_FROM_FAVORITE_ORG",
  "MEMBER_INVITED",
  "RATING_REQUEST",
  "GENERIC",
]);
export type NotificationTypeValue = z.infer<typeof notificationTypeSchema>;

export const notificationTemplateSchema = z.object({
  id: uuidSchema,
  type: notificationTypeSchema,
  locale: z.string(),
  title: z.string(),
  body: z.string(),
  actionUrlTemplate: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  variables: z.array(z.string()).nullable().optional(),
  isCustomised: z.boolean(),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});
export type NotificationTemplate = z.infer<typeof notificationTemplateSchema>;

export const adminNotificationTemplateListResponseSchema = z.object({
  items: z.array(notificationTemplateSchema),
});

export const adminUpdateNotificationTemplateRequestSchema = z.object({
  title: z.string().min(1).max(160),
  body: z.string().min(1).max(1000),
  actionUrlTemplate: z.string().max(500).nullable().optional(),
});
export type AdminUpdateNotificationTemplateRequest = z.infer<
  typeof adminUpdateNotificationTemplateRequestSchema
>;

// Recipient selectors — designed to grow. Each entry is its own object so
// the UI can mix multiple selectors in one send (e.g. "all workers + these
// 3 phone numbers"), and we can add new modes without breaking the API.
export const adminNotificationRecipientSelectorSchema = z.discriminatedUnion(
  "mode",
  [
    z.object({
      mode: z.literal("users"),
      userIds: z.array(uuidSchema).min(1).max(500),
    }),
    z.object({
      mode: z.literal("segment"),
      segment: z.enum(["ALL", "WORKER", "EMPLOYER", "ADMIN"]),
    }),
    z.object({
      mode: z.literal("identifiers"),
      // Email or phone — server resolves to userIds.
      identifiers: z.array(z.string().min(3).max(120)).min(1).max(500),
    }),
  ],
);
export type AdminNotificationRecipientSelector = z.infer<
  typeof adminNotificationRecipientSelectorSchema
>;

export const adminSendNotificationRequestSchema = z.object({
  title: z.string().min(1).max(160),
  body: z.string().min(1).max(1000),
  actionUrl: z.string().max(500).nullable().optional(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
  // Defaults to GENERIC; admins can pick a specific type when re-using an
  // existing category (e.g. send a one-off POSTING_EXPIRING reminder).
  type: notificationTypeSchema.optional(),
  recipients: z.array(adminNotificationRecipientSelectorSchema).min(1).max(10),
});
export type AdminSendNotificationRequest = z.infer<
  typeof adminSendNotificationRequestSchema
>;

export const adminSendNotificationResponseSchema = z.object({
  resolvedUserCount: z.number().int().min(0),
  deliveredCount: z.number().int().min(0),
  skippedCount: z.number().int().min(0),
});
export type AdminSendNotificationResponse = z.infer<
  typeof adminSendNotificationResponseSchema
>;

// ---------------------------------------------------------------------------
// Email / notification preferences (docs/05-email-engagement.md)
// ---------------------------------------------------------------------------

// Per-category email toggles + master push toggle. Mirrors the editable fields
// of NotificationPreference (TRANSACTIONAL is always-on, not represented here).
export const notificationPreferenceSchema = z.object({
  emailInterview: z.boolean(),
  emailApplicationStatus: z.boolean(),
  emailRecommendations: z.boolean(),
  emailEmployerDigest: z.boolean(),
  emailProductMarketing: z.boolean(),
  pushEnabled: z.boolean(),
});
export type NotificationPreference = z.infer<
  typeof notificationPreferenceSchema
>;

export const updateNotificationPreferenceRequestSchema =
  notificationPreferenceSchema.partial();
export type UpdateNotificationPreferenceRequest = z.infer<
  typeof updateNotificationPreferenceRequestSchema
>;

export const notificationPreferenceResponseSchema = z.object({
  preference: notificationPreferenceSchema,
});
export type NotificationPreferenceResponse = z.infer<
  typeof notificationPreferenceResponseSchema
>;

// Public (token-based) preference view returned by GET /u/:token — no PII,
// just the toggles so the unsubscribe page can render checkboxes.
export const publicPreferenceResponseSchema = z.object({
  preference: notificationPreferenceSchema,
});
export type PublicPreferenceResponse = z.infer<
  typeof publicPreferenceResponseSchema
>;

// One-click / page unsubscribe payload. `category` lets the List-Unsubscribe
// one-click POST turn off a single category (or "all").
export const unsubscribeRequestSchema = z
  .object({
    category: z
      .enum([
        "INTERVIEW",
        "APPLICATION_STATUS",
        "RECOMMENDATIONS",
        "EMPLOYER_DIGEST",
        "PRODUCT_MARKETING",
        "all",
      ])
      .optional(),
  })
  .merge(updateNotificationPreferenceRequestSchema);
export type UnsubscribeRequest = z.infer<typeof unsubscribeRequestSchema>;
